import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, createElementBlock as _createElementBlock, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle, withCtx as _withCtx } from "vue"

const _hoisted_1 = ["id"]
const _hoisted_2 = { class: "c-info-card-row__header" }
const _hoisted_3 = { class: "c-info-card-row__carousel" }
const _hoisted_4 = {
  key: 0,
  class: "c-info-card-row__controls"
}
const _hoisted_5 = {
  key: 0,
  class: "c-info-card-row__title"
}
const _hoisted_6 = ["innerHTML"]
const _hoisted_7 = {
  key: 2,
  class: "c-info-card-row__media"
}
const _hoisted_8 = ["href", "target", "rel", "aria-label"]
const _hoisted_9 = {
  key: 0,
  class: "c-info-card-row__title"
}
const _hoisted_10 = ["innerHTML"]
const _hoisted_11 = {
  key: 2,
  class: "c-info-card-row__media"
}
const _hoisted_12 = ["href", "target", "rel", "aria-label"]

import { computed, nextTick, ref } from 'vue';
import { Navigation, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/vue';
import { RichText, Text as JssText } from '@sitecore-jss/sitecore-jss-vue';
import 'swiper/css';
import 'swiper/css/pagination';
import type { InfoCardRowCardFields, InfoCardRowProps } from '@/types/components/InfoCardRow';
import type { ScItem } from '@/types/sitecore';
import utils from '@/utils';

interface NormalizedCard extends ScItem<InfoCardRowCardFields> {
  fields: InfoCardRowCardFields;
  href: string;
  target: string | undefined;
  linkLabel: string;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'InfoCardRow',
  props: {
    dataSource: {},
    fields: {},
    rendering: {},
    params: {}
  },
  setup(__props: any) {

const props = __props;
const previousButton = ref<HTMLElement | null>(null);
const nextButton = ref<HTMLElement | null>(null);
const fields = computed(() => props.fields);
const swiperModules = [Pagination, Navigation];
const isCardStyle = computed(() => Boolean(fields.value?.isCardStyle?.value));
const rootClasses = computed(() => {
  const baseClasses = utils.overall.baseComClass(props.fields, 'c-info-card-row');
  return [
    baseClasses,
    {
      'c-info-card-row--card-style': isCardStyle.value,
      'c-info-card-row--without-header': !hasHeader.value,
      'c-info-card-row--hide-pagination-mobile': cards.value.length <= 1,
      'c-info-card-row--hide-pagination-tablet': cards.value.length <= 2,
      'c-info-card-row--hide-pagination-desktop': cards.value.length <= 5,
    },
  ];
});
const hasHeader = computed(
  () => Boolean(fields.value?.title?.value) || Boolean(fields.value?.description?.value)
);

const paginationOptions = {
  clickable: true,
  bulletClass: 'icr-swiper-dot',
  bulletActiveClass: 'is-active',
};

const navigationOptions = computed(() =>
  cards.value.length > 5
    ? {
        prevEl: previousButton.value,
        nextEl: nextButton.value,
      }
    : false
);

const onSwiper = async (instance: any) => {
  await nextTick();
  if (!previousButton.value || !nextButton.value || !instance.navigation) return;

  instance.params.navigation = {
    ...instance.params.navigation,
    prevEl: previousButton.value,
    nextEl: nextButton.value,
  };
  instance.navigation.destroy();
  instance.navigation.init();
  instance.navigation.update();
};

// hover 时先锁定卡片为未 hover 的高度，再加 is-hovered 类应用 hover 样式，
// 确保卡片高度不随文案展开而变化，也不会触发 swiper stretch 去拉高其余卡片
const onCardEnter = (event: MouseEvent) => {
  const card = event.currentTarget as HTMLElement;
  card.style.height = `${card.offsetHeight}px`;
  card.classList.add('is-hovered');
};

const onCardLeave = (event: MouseEvent) => {
  const card = event.currentTarget as HTMLElement;
  card.classList.remove('is-hovered');
  card.style.height = '';
};

const swiperBreakpoints = computed(() => ({
  768: {
    slidesPerView: Math.min(cards.value.length, 2),
    spaceBetween: 24,
  },
  880: {
    slidesPerView: Math.min(cards.value.length, 3),
    spaceBetween: 16,
  },
  1200: {
    slidesPerView: Math.min(cards.value.length, 5),
    spaceBetween: 16,
  },
}));

const rootStyle = computed(() => utils.overall?.baseComStyle?.(fields.value) || {});

const rawCards = computed<ScItem<InfoCardRowCardFields>[]>(() => {
  const cardItems = utils.sc.getFieldObj(fields.value, 'cards');
  return Array.isArray(cardItems) ? cardItems : [];
});

const cards = computed<NormalizedCard[]>(() =>
  rawCards.value.reduce<NormalizedCard[]>((result, item, index) => {
    const itemFields = item.fields;
    if (!itemFields) return result;

    const linkValue = itemFields.link?.value || {};
    const title = itemFields.title?.value || `Info card ${index + 1}`;

    result.push({
      ...item,
      fields: itemFields,
      href: linkValue.href || '',
      target: linkValue.target,
      linkLabel: linkValue.text || linkValue.title || title,
    });

    return result;
  }, [])
);

const cardGridStyle = computed(() => {
  const count = cards.value.length;
  const createRowVariables = (prefix: string, maxColumns: number) => {
    const columns = Math.min(count, maxColumns);
    return {
      [`--${prefix}-columns`]: `${columns}`,
      [`--${prefix}-gaps`]: `${Math.max(columns - 1, 0) * 16}px`,
    };
  };

  return {
    ...createRowVariables('icr-tablet', 2),
    ...createRowVariables('icr-landscape', 3),
    ...createRowVariables('icr-desktop', 5),
  };
});

const cardGridClasses = computed(() => ({
  'c-info-card-row__grid--multi-row-mobile': cards.value.length > 1,
  'c-info-card-row__grid--multi-row-tablet': cards.value.length > 2,
  'c-info-card-row__grid--multi-row-landscape': cards.value.length > 3,
  'c-info-card-row__grid--multi-row-desktop': cards.value.length > 5,
}));

return (_ctx: any,_cache: any) => {
  const _component_JssRichText = _resolveComponent("JssRichText")!
  const _component_AdaptiveImage = _resolveComponent("AdaptiveImage")!
  const _component_ContentGrid = _resolveComponent("ContentGrid")!

  return (fields.value && cards.value.length)
    ? (_openBlock(), _createElementBlock("section", {
        key: 0,
        class: _normalizeClass(["c-info-card-row", rootClasses.value]),
        style: _normalizeStyle(rootStyle.value),
        id: __props.rendering?.dataSource
      }, [
        _createVNode(_component_ContentGrid, {
          "desktop-default-grid": [2, 20],
          "mobile-default-grid": [1, 14]
        }, {
          default: _withCtx(() => [
            _createElementVNode("div", _hoisted_2, [
              _createVNode(_component_JssRichText, {
                class: "c-info-card-row__heading",
                field: fields.value?.title,
                tag: "h2"
              }, null, 8, ["field"]),
              (fields.value?.description)
                ? (_openBlock(), _createBlock(_unref(RichText), {
                    key: 0,
                    class: "c-info-card-row__intro",
                    field: fields.value.description
                  }, null, 8, ["field"]))
                : _createCommentVNode("", true)
            ]),
            _createElementVNode("div", _hoisted_3, [
              (!isCardStyle.value && cards.value.length > 5)
                ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                    _createElementVNode("button", {
                      ref_key: "previousButton",
                      ref: previousButton,
                      class: "c-info-card-row__control c-info-card-row__control--previous",
                      type: "button",
                      "aria-label": "Previous cards"
                    }, [...(_cache[0] || (_cache[0] = [
                      _createElementVNode("span", { "aria-hidden": "true" }, "←", -1)
                    ]))], 512),
                    _createElementVNode("button", {
                      ref_key: "nextButton",
                      ref: nextButton,
                      class: "c-info-card-row__control c-info-card-row__control--next",
                      type: "button",
                      "aria-label": "Next cards"
                    }, [...(_cache[1] || (_cache[1] = [
                      _createElementVNode("span", { "aria-hidden": "true" }, "→", -1)
                    ]))], 512)
                  ]))
                : _createCommentVNode("", true),
              (isCardStyle.value)
                ? (_openBlock(), _createElementBlock("div", {
                    key: 1,
                    class: _normalizeClass(["c-info-card-row__grid", cardGridClasses.value]),
                    style: _normalizeStyle(cardGridStyle.value)
                  }, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(cards.value, (card) => {
                      return (_openBlock(), _createElementBlock("article", {
                        key: card.id,
                        class: "c-info-card-row__card"
                      }, [
                        (card.fields.title?.value)
                          ? (_openBlock(), _createElementBlock("h3", _hoisted_5, [
                              _createElementVNode("strong", {
                                innerHTML: card.fields.title.value
                              }, null, 8, _hoisted_6)
                            ]))
                          : _createCommentVNode("", true),
                        (card.fields?.description)
                          ? (_openBlock(), _createBlock(_unref(RichText), {
                              key: 1,
                              class: "c-info-card-row__description",
                              field: card.fields.description
                            }, null, 8, ["field"]))
                          : _createCommentVNode("", true),
                        (card.fields.image)
                          ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                              _createVNode(_component_AdaptiveImage, {
                                class: "c-info-card-row__image",
                                field: card.fields.image,
                                show: true,
                                "is-background": false,
                                "enable-scroll-detection": false,
                                "show-image-redius": false
                              }, null, 8, ["field"])
                            ]))
                          : _createCommentVNode("", true),
                        (card.href)
                          ? (_openBlock(), _createElementBlock("a", {
                              key: 3,
                              class: "c-info-card-row__link",
                              href: card.href,
                              target: card.target,
                              rel: card.target === '_blank' ? 'noopener noreferrer' : undefined,
                              "aria-label": card.linkLabel
                            }, [...(_cache[2] || (_cache[2] = [
                              _createElementVNode("span", { "aria-hidden": "true" }, null, -1)
                            ]))], 8, _hoisted_8))
                          : _createCommentVNode("", true)
                      ]))
                    }), 128))
                  ], 6))
                : (_openBlock(), _createBlock(_unref(Swiper), {
                    key: 2,
                    class: "c-info-card-row__swiper",
                    modules: swiperModules,
                    "slides-per-view": 1.12,
                    "space-between": 16,
                    pagination: paginationOptions,
                    navigation: navigationOptions.value,
                    breakpoints: swiperBreakpoints.value,
                    onSwiper: onSwiper
                  }, {
                    default: _withCtx(() => [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(cards.value, (card) => {
                        return (_openBlock(), _createBlock(_unref(SwiperSlide), {
                          key: card.id,
                          class: "c-info-card-row__slide"
                        }, {
                          default: _withCtx(() => [
                            _createElementVNode("article", {
                              class: "c-info-card-row__card",
                              onMouseenter: onCardEnter,
                              onMouseleave: onCardLeave
                            }, [
                              _cache[4] || (_cache[4] = _createElementVNode("span", {
                                class: "c-info-card-row__mark",
                                "aria-hidden": "true"
                              }, null, -1)),
                              (card.fields.title?.value)
                                ? (_openBlock(), _createElementBlock("h3", _hoisted_9, [
                                    _createElementVNode("strong", {
                                      innerHTML: card.fields.title.value
                                    }, null, 8, _hoisted_10)
                                  ]))
                                : _createCommentVNode("", true),
                              (card.fields?.description)
                                ? (_openBlock(), _createBlock(_unref(RichText), {
                                    key: 1,
                                    class: "c-info-card-row__description",
                                    field: card.fields.description
                                  }, null, 8, ["field"]))
                                : _createCommentVNode("", true),
                              (card.fields.image)
                                ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
                                    _createVNode(_component_AdaptiveImage, {
                                      class: "c-info-card-row__image",
                                      field: card.fields.image,
                                      show: true,
                                      "is-background": false,
                                      "enable-scroll-detection": false,
                                      "show-image-redius": false
                                    }, null, 8, ["field"])
                                  ]))
                                : _createCommentVNode("", true),
                              (card.href)
                                ? (_openBlock(), _createElementBlock("a", {
                                    key: 3,
                                    class: "c-info-card-row__link",
                                    href: card.href,
                                    target: card.target,
                                    rel: card.target === '_blank' ? 'noopener noreferrer' : undefined,
                                    "aria-label": card.linkLabel
                                  }, [...(_cache[3] || (_cache[3] = [
                                    _createElementVNode("span", { "aria-hidden": "true" }, null, -1)
                                  ]))], 8, _hoisted_12))
                                : _createCommentVNode("", true)
                            ], 32)
                          ]),
                          _: 2
                        }, 1024))
                      }), 128))
                    ]),
                    _: 1
                  }, 8, ["navigation", "breakpoints"]))
            ])
          ]),
          _: 1
        })
      ], 14, _hoisted_1))
    : _createCommentVNode("", true)
}
}

})